import { motion } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";

const pageVariants = {
  initial: { opacity: 0, rotateY: 8 },
  animate: { opacity: 1, rotateY: 0 },
  exit: { opacity: 0, rotateY: -8 },
};

const services = [
  {
    icon: "🏛️",
    title: "Wall Murals",
    desc: "Luxury handcrafted wall mural solutions — from concept to grand reveal. We transform bare walls into breathtaking masterpieces that tell your unique story.",
    tag: "Signature",
    color: "#e8503a",
    grad: "linear-gradient(135deg,#e8503a,#f0a090)",
    bg: "#fff5f3",
    route: "/services/wall-murals",
  },
  {
    icon: "🗿",
    title: "Wall Sculptures",
    desc: "Bespoke three-dimensional wall sculptures crafted with precision and artistry. Each piece brings depth, texture, and soul to any interior or exterior space.",
    tag: "Sculpture",
    color: "#2a7abf",
    grad: "linear-gradient(135deg,#2a7abf,#6aaee0)",
    bg: "#f0f6ff",
    route: "/services/wall-sculptures",
  },
  {
    icon: "🖼️",
    title: "Large Canvas",
    desc: "Monumental canvas paintings that command attention. Custom large-scale artwork for corporate spaces, luxury residences, hotels, and prestigious galleries.",
    tag: "Fine Art",
    color: "#7c5cbf",
    grad: "linear-gradient(135deg,#7c5cbf,#b090e0)",
    bg: "#f8f5ff",
    route: "/services/large-canvas",
  },
  {
    icon: "🏠",
    title: "Home Decors",
    desc: "Enhance your interiors with thoughtfully curated home décor pieces that add charm, comfort, and character to every space.",
    tag: "Crafting Spaces with Style & Soul",
    color: "#d6246e",
    grad: "linear-gradient(135deg,#d6246e,#f08cb0)",
    bg: "#fff0f6",
    route: "/services/home-decors",
  },
  {
    icon: "🔩",
    title: "Fiber Glass & GRC Murals",
    desc: "Durable, weather-resistant Fiber Glass and Glass Reinforced Concrete murals — engineered for longevity and crafted for grandeur in any environment.",
    tag: "Industrial Art",
    color: "#c9982a",
    grad: "linear-gradient(135deg,#c9982a,#f0c84a)",
    bg: "#fffbf0",
    route: "/services/fiberglass-grc",
  },
  {
    icon: "🌟",
    title: "Artistic Containers",
    desc: "Collaborative creative projects that push boundaries — art installations, cultural exhibitions, brand activations, and immersive artistic experiences.",
    tag: "Containers",
    color: "#d6246e",
    grad: "linear-gradient(135deg,#d6246e,#f08cb0)",
    bg: "#fff0f6",
    route: "/services/artistic-containers",
  },
  {
    icon: "🏗️",
    title: "Facade & Glass Murals",
    desc: "Architectural facade art and stunning glass mural installations that transform buildings into iconic visual landmarks seen and admired by all who pass.",
    tag: "Architecture",
    color: "#0ab5a8",
    grad: "linear-gradient(135deg,#0ab5a8,#60d5c8)",
    bg: "#f0fffe",
    route: "/services/facades-glass",
  },
  {
    icon: "💍",
    title: "Wedding Art & Décor",
    desc: "Creating breathtaking hand-crafted art installations and bespoke décor for your most cherished day — where every detail tells your unique love story.",
    tag: "Celebrate in Art",
    color: "#c9356e",
    grad: "linear-gradient(135deg,#c9356e,#f4a0c0)",
    bg: "#fff0f6",
    route: "/services/wedding-art",
  },
];

const dripsColors = ["#e8503a","#c9982a","#7c5cbf","#0ab5a8","#d6246e","#2a7abf","#e8503a"];
const dripsHeights = [60, 80, 50, 90, 65, 75, 55];

function Services() {
  const navigate = useNavigate();
  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.6, ease: "easeInOut" }}
    >
      <section
        style={{ minHeight:"55vh", background:"linear-gradient(150deg,#0f0520 0%,#1a0a2e 35%,#200a18 70%,#0a1828 100%)", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", textAlign:"center", padding:"80px 5% 60px", position:"relative", overflow:"hidden" }}
      >
        {[
          { size:350, color:"#e8503a", top:"-100px", left:"-80px", delay:0 },
          { size:250, color:"#7c5cbf", bottom:"-60px", right:"-60px", delay:1.5 },
          { size:180, color:"#0ab5a8", top:"30%", right:"8%", delay:3 },
          { size:200, color:"#d6246e", bottom:"10%", left:"10%", delay:2 },
        ].map((b, i) => (
          <motion.div key={i} animate={{ scale:[1,1.2,1], rotate:[0,20,0], opacity:[0.6,0.9,0.6] }} transition={{ repeat:Infinity, duration:6, delay:b.delay, ease:"easeInOut" }} style={{ position:"absolute", width:b.size, height:b.size, borderRadius:"50%", background:`radial-gradient(circle,${b.color},transparent 70%)`, top:b.top, bottom:b.bottom, left:b.left, right:b.right, pointerEvents:"none", mixBlendMode:"screen" }} />
        ))}
        <motion.p initial={{opacity:0}} animate={{opacity:1}} style={{fontFamily:"'Satisfy',cursive",color:"#c9982a",fontSize:"1.1rem",letterSpacing:"3px",marginBottom:16}}>✦ What We Create ✦</motion.p>
        <motion.h1 initial={{opacity:0,y:30}} animate={{opacity:1,y:0}} transition={{delay:0.2,duration:0.9}} style={{fontFamily:"'Cormorant Garamond',serif",fontSize:"clamp(2.8rem,7vw,5rem)",fontWeight:700,fontStyle:"italic",color:"#fff",lineHeight:1.1}}>
          Our <span style={{color:"#c9982a"}}>Services</span>
        </motion.h1>
        <motion.p initial={{opacity:0}} animate={{opacity:1}} transition={{delay:0.4}} style={{color:"rgba(255,255,255,.6)",fontWeight:300,fontSize:"1.05rem",maxWidth:500,margin:"18px auto 0",lineHeight:1.9}}>
          Every surface is a canvas. Every wall, a story. Every project, a masterpiece born from passion and precision.
        </motion.p>
        <motion.div initial={{opacity:0}} animate={{opacity:1}} transition={{delay:0.6}} style={{display:"flex",justifyContent:"center",gap:6,marginTop:40}}>
          {dripsColors.map((c, i) => (
            <motion.div key={i} animate={{scaleY:[1,1.15,1],y:[0,4,0]}} transition={{repeat:Infinity,duration:2,delay:i*0.2,ease:"easeInOut"}} style={{width:10,height:dripsHeights[i],borderRadius:40,background:c,transformOrigin:"top"}} />
          ))}
        </motion.div>
      </section>

      <section style={{padding:"80px 4%",background:"var(--cream,#fdf8f0)"}}>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(290px,1fr))",gap:28}}>
          {services.map((svc, i) => (
            <motion.div
              key={i}
              initial={{opacity:0,y:50}}
              whileInView={{opacity:1,y:0}}
              viewport={{once:true}}
              transition={{delay:i*0.1,duration:0.6}}
              whileHover={{y:-14,rotate:-0.8,boxShadow:"0 28px 60px rgba(0,0,0,.16)",transition:{duration:0.3}}}
              style={{background:"#fff",borderRadius:28,overflow:"hidden",boxShadow:"0 6px 30px rgba(0,0,0,.08)",cursor:"pointer"}}
              onClick={() => navigate(svc.route)}
            >
              <div style={{padding:"36px 32px 28px",background:svc.bg,position:"relative",overflow:"hidden"}}>
                <motion.div whileHover={{scale:1.4,rotate:20}} transition={{duration:0.5}} style={{position:"absolute",width:160,height:160,borderRadius:"50%",background:svc.color,opacity:0.18,right:-40,top:-40}} />
                <div style={{marginBottom:14}}>
                  {[1,0.5,0.3].map((op,d)=>(
                    <motion.span key={d} animate={{scale:[1,1.4,1]}} transition={{repeat:Infinity,duration:2.5,delay:i*0.3+d*0.4}} style={{display:"inline-block",width:14-d*3,height:14-d*3,borderRadius:"50%",background:svc.color,opacity:op,marginRight:6}} />
                  ))}
                </div>
                <span style={{display:"inline-block",padding:"5px 14px",borderRadius:20,background:svc.grad,color:"#fff",fontSize:".7rem",fontWeight:700,letterSpacing:"1.5px",textTransform:"uppercase",marginBottom:20}}>{svc.tag}</span>
                <motion.div whileHover={{scale:1.15,rotate:-5}} style={{fontSize:"3rem",marginBottom:16}}>{svc.icon}</motion.div>
                <h3 style={{fontFamily:"'Cormorant Garamond',serif",fontSize:"1.6rem",fontWeight:700,color:"var(--ink,#1a0a1e)",marginBottom:12}}>{svc.title}</h3>
                <p style={{color:"#6a5a7a",lineHeight:1.85,fontSize:".93rem",fontWeight:300}}>{svc.desc}</p>
              </div>
              <div style={{padding:"22px 32px 28px",borderTop:"1px dashed rgba(0,0,0,.07)",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <motion.div initial={{scaleX:0}} whileInView={{scaleX:1}} viewport={{once:true}} transition={{delay:0.4+i*0.1,duration:0.6}} style={{height:5,borderRadius:3,flex:1,marginRight:16,background:svc.grad,transformOrigin:"left"}} />
                <motion.button whileHover={{gap:12}} style={{fontSize:".8rem",fontWeight:600,letterSpacing:".5px",border:"none",background:"transparent",cursor:"pointer",display:"flex",alignItems:"center",gap:6,color:"var(--ink,#1a0a1e)",whiteSpace:"nowrap",fontFamily:"'DM Sans',sans-serif"}}>
                  Explore
                  <motion.span whileHover={{rotate:45}} style={{width:28,height:28,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",background:svc.grad,color:"#fff",fontSize:".9rem"}}>→</motion.span>
                </motion.button>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <section style={{background:"linear-gradient(120deg,#1a0a2e,#0a1828)",padding:"60px 5%",textAlign:"center",overflow:"hidden"}}>
        <div style={{overflow:"hidden",whiteSpace:"nowrap"}}>
          <motion.span animate={{x:[0,"-50%"]}} transition={{repeat:Infinity,duration:18,ease:"linear"}} style={{display:"inline-block",fontFamily:"'Satisfy',cursive",fontSize:"1.3rem",color:"rgba(255,255,255,.25)",letterSpacing:"4px"}}>
            {"🏠Residential Art Interiors \u00a0✦\u00a0🎨 Wall Murals \u00a0✦\u00a0 🗿 Sculptures \u00a0✦\u00a0 🖼️ Large Canvas \u00a0✦\u00a0 🔩 Fiber Glass & GRC Murals \u00a0✦\u00a0 🌟 Artistic Containers \u00a0✦\u00a0 🏗️ Facade & Glass Murals \u00a0✦\u00a0🌟Home Decors ".repeat(2)}
          </motion.span>
        </div>
        <motion.p initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} style={{fontFamily:"'Cormorant Garamond',serif",fontSize:"clamp(1.5rem,3.5vw,2.4rem)",fontStyle:"italic",color:"#fff",maxWidth:700,margin:"28px auto 0",lineHeight:1.6}}>
          <span style={{color:"#c9982a"}}>"Art is the only way to run away</span>{" "}without leaving home — and we bring that escape to every wall we touch."
        </motion.p>
      </section>

      <section style={{background:"linear-gradient(150deg,#0f0520,#1a0a2e)",padding:"100px 5%",textAlign:"center",position:"relative",overflow:"hidden"}}>
        {[{size:200,color:"rgba(201,152,42,.15)",top:"20%",right:"5%",delay:0},{size:120,color:"rgba(124,92,191,.2)",bottom:"15%",left:"8%",delay:2}].map((c,i)=>(
          <motion.div key={i} animate={{y:[0,-20,0]}} transition={{repeat:Infinity,duration:7,delay:c.delay,ease:"easeInOut"}} style={{position:"absolute",width:c.size,height:c.size,borderRadius:"50%",border:`2px solid ${c.color}`,top:c.top,bottom:c.bottom,left:c.left,right:c.right,pointerEvents:"none"}} />
        ))}
        <motion.h2 initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} style={{fontFamily:"'Cormorant Garamond',serif",fontSize:"clamp(2.2rem,5vw,4rem)",fontStyle:"italic",fontWeight:700,color:"#fff",marginBottom:18}}>
          Ready to <span style={{color:"#c9982a"}}>Transform</span><br />Your Space?
        </motion.h2>
        <motion.p initial={{opacity:0}} whileInView={{opacity:1}} viewport={{once:true}} style={{color:"rgba(255,255,255,.6)",fontWeight:300,fontSize:"1rem",maxWidth:440,margin:"0 auto 40px",lineHeight:1.8}}>
          Let's create something extraordinary together — from a single wall to an entire building facade.
        </motion.p>
        <Link to="/contact" style={{textDecoration:"none"}}>
          <motion.span whileHover={{y:-5,boxShadow:"0 20px 50px rgba(201,152,42,.55)"}} whileTap={{scale:0.97}} style={{display:"inline-block",padding:"16px 48px",borderRadius:"50px",border:"none",background:"linear-gradient(135deg,#c9982a,#f0c84a)",color:"#1a0a1e",fontWeight:700,fontSize:"1rem",cursor:"pointer",boxShadow:"0 10px 36px rgba(201,152,42,.4)",textDecoration:"none",fontFamily:"'DM Sans',sans-serif"}}>
            Contact Us →
          </motion.span>
        </Link>
      </section>
    </motion.div>
  );
}
export default Services;
